import { Seo, Eyebrow } from "@/components/site/primitives";
import { Reveal, Stagger, Item } from "@/components/site/motion";
import { PageHero, Section, Pill } from "@/components/site/pageparts";
import { CopyInstall, VersionBadge } from "@/components/site/CopyInstall";

const RELEASES = [
  {
    v: "v0.7.0",
    date: "17 Jul 2026",
    status: "Live",
    tone: "signal",
    notes: [
      "Content-addressed ingestion pipeline — 100% reproducible chunk hashes on re-ingest.",
      "The Context Rehydrator: semantic + temporal fusion (relational built, weight-0 by default).",
      "Local knowledge graph populated on ingest; `upii knowledge --graph` renders it.",
      "12 CLI commands: ingest, search, ask, write, watch, inbox, sources, knowledge, metrics, doctor and more.",
      "Evaluation harness published — reproduce Recall@10 = 0.958 yourself.",
    ],
  },
];

const ROADMAP = [
  { stage: "In beta", t: "Relational retrieval + ambient inbox", d: "Knowledge-graph re-ranking across people, projects and organisations, plus consent-gated ambient capture through an approval inbox." },
  { stage: "Coming soon", t: "MCP bridge for agents", d: "Expose smritium_search / smritium_ask / smritium_list_sources so Claude, Cursor and other agents use only approved, cited chunks." },
  { stage: "Coming soon", t: "Cmd+K everywhere + encrypted sync", d: "A lightweight overlay puts memory one keystroke away, with multi-device sync over LAN / encrypted P2P — no cloud in the loop." },
  { stage: "Coming soon", t: "Mail & calendar connectors", d: "Consent-gated capture from mail (.mbox / .eml) and calendar (.ics), with debouncing, dedup and delete-handling." },
  { stage: "Planned", t: "Smritium Membrane — Enterprise", d: "Org-wide egress audit + PII redaction, on-prem / air-gapped deployment, and DPDP / GDPR compliance reporting." },
  { stage: "Planned", t: "Answer verification & upii forget", d: "Abstention when confidence is low, and user-facing verified deletion — remove a memory and prove it is gone." },
];

export default function Changelog() {
  return (
    <div data-testid="changelog-page">
      <Seo title="Smritium — Changelog" description="Release notes for the Smritium / upii core. Current live version and the road ahead." />
      <PageHero
        eyebrow="Smritium · Changelog"
        title="The open-source core is live. Here's what shipped, and what's next."
        lead="upii is an early preview with a stable core. Interfaces are still moving; the invariants — local-first, deterministic, cite-or-abstain — are not."
      >
        <div className="flex flex-wrap items-center gap-4">
          <div className="w-full max-w-xs">
            <CopyInstall variant="inline" />
          </div>
          <VersionBadge />
        </div>
      </PageHero>

      <Section>
        <Reveal>
          <Eyebrow>Releases</Eyebrow>
        </Reveal>
        {RELEASES.map((r) => (
          <Reveal key={r.v} delay={0.05} className="mt-10 border border-white/10 bg-white/[0.015] p-8 md:p-10">
            <div className="flex flex-wrap items-center gap-4">
              <span className="font-serif text-2xl text-bone">{r.v}</span>
              <Pill tone={r.tone}>{r.status}</Pill>
              <span className="font-mono text-xs text-mist">{r.date}</span>
            </div>
            <ul className="mt-8 space-y-4">
              {r.notes.map((n, i) => (
                <li key={i} className="flex gap-4 text-[15px] leading-relaxed text-mist">
                  <span className="mt-2.5 h-1.5 w-1.5 flex-shrink-0 bg-saffron" />
                  {n}
                </li>
              ))}
            </ul>
          </Reveal>
        ))}
      </Section>

      <Section className="border-t border-white/10">
        <Reveal>
          <Eyebrow>The road ahead</Eyebrow>
          <h2 className="mt-5 font-serif text-3xl tracking-tighter text-bone md:text-5xl">What's shipping next.</h2>
        </Reveal>
        <Stagger className="mt-14 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
          {ROADMAP.map((r) => (
            <Item key={r.t} className="bg-ink p-7">
              <div className="font-mono text-[11px] uppercase tracking-[0.14em] text-saffron">{r.stage}</div>
              <h3 className="mt-4 font-serif text-lg text-bone">{r.t}</h3>
              <p className="mt-3 text-sm leading-relaxed text-mist">{r.d}</p>
            </Item>
          ))}
        </Stagger>
      </Section>
    </div>
  );
}
